import { Link } from 'react-router-dom';
import type { FlowStageDetail } from '@interfaces/IFlowView';
import {
  ActivityIcon,
  ArrowRightIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  FileTextIcon,
  HollowCircleIcon,
  PackageIcon,
  ShieldIcon,
  TruckIcon,
  UsersIcon,
  WarningTriangleIcon,
  WrenchIcon,
} from '@utils/icons';
import styles from './StageDetail.module.css';

type IStageDetailProps = {
  eyebrow: string;
  detail: FlowStageDetail;
  /** Fully-resolved href for the action button. When null the button
      renders disabled (no corresponding D-ORCA page exists yet). */
  actionHref: string | null;
  labels: {
    owner: string;
    due: string;
    completion: string;
    dataSource: string;
    items: string;
    blockers: string;
  };
};

function StageGlyph({ k }: { k: FlowStageDetail['key'] }) {
  switch (k) {
    case 'order':    return <PackageIcon size={18} />;
    case 'deliver':  return <TruckIcon size={18} />;
    case 'schedule': return <CalendarIcon size={18} />;
    case 'install':  return <WrenchIcon size={18} />;
    case 'verify':   return <ShieldIcon size={18} />;
  }
}

export function StageDetail({ eyebrow, detail, actionHref, labels }: IStageDetailProps) {
  return (
    <section className={styles.panel}>
      <header className={styles.head}>
        <div className={styles.heading}>
          <span className={styles.glyphBox}>
            <StageGlyph k={detail.key} />
          </span>
          <div className={styles.headingText}>
            <span className={styles.eyebrow}>{eyebrow}</span>
            <h2 className={styles.title}>{detail.title}</h2>
          </div>
        </div>
        {actionHref ? (
          <Link to={actionHref} className={styles.action}>
            <ArrowRightIcon size={14} />
            {detail.actionLabel}
          </Link>
        ) : (
          <button type="button" className={styles.action} disabled>
            <ArrowRightIcon size={14} />
            {detail.actionLabel}
          </button>
        )}
      </header>

      <div className={styles.facts}>
        <div className={styles.fact}>
          <span className={styles.factLabel}>
            <UsersIcon size={12} /> {labels.owner}
          </span>
          <span className={styles.factValue}>{detail.owner}</span>
        </div>
        <div className={styles.fact}>
          <span className={styles.factLabel}>
            <ClockIcon size={12} /> {labels.due}
          </span>
          <span className={styles.factValue}>{detail.dueLabel}</span>
        </div>
        <div className={styles.fact}>
          <span className={styles.factLabel}>
            <ActivityIcon size={12} /> {labels.completion}
          </span>
          <span className={styles.factValue}>{detail.completionLabel}</span>
        </div>
        <div className={styles.fact}>
          <span className={styles.factLabel}>
            <FileTextIcon size={12} /> {labels.dataSource}
          </span>
          <span className={styles.factValue}>{detail.dataSource}</span>
        </div>
      </div>

      <div className={styles.itemsBlock}>
        <div className={styles.itemsHead}>
          <span className={styles.itemsTitle}>{labels.items}</span>
          <span className={styles.itemsCount}>{detail.itemsLabel}</span>
        </div>
        <ul className={styles.items}>
          {detail.items.map((item) => (
            <li key={item.id} className={styles.item} data-done={item.done}>
              {item.done ? <CheckCircleIcon size={14} /> : <HollowCircleIcon size={14} />}
              {item.label}
            </li>
          ))}
        </ul>
      </div>

      {detail.blockers.length > 0 && (
        <div className={styles.blockers}>
          <span className={styles.blockersTitle}>
            <WarningTriangleIcon size={14} />
            {labels.blockers}
          </span>
          <ul className={styles.blockersList}>
            {detail.blockers.map((b) => (
              <li key={b.id} className={styles.blocker} data-severity={b.severity}>
                <span className={styles.blockerDot} aria-hidden />
                {b.text}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
